import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAutoSave } from '../hooks/useAutoSave';
import AutoSaveIndicator from './ui/AutoSaveIndicator';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from './ui/ConfirmDialog';
import {
  Mail,
  Phone,
  MapPin,
  Loader2,
  Upload,
  Trash2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { PUBLIC_SITE_URL } from '../config/api';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { apiService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { LogoWarnings } from './LogoWarnings';

import { NON_EURO_COUNTRY_CODES, countryOptions } from '../lib/countries';

interface OrganizationData {
  id: string;
  name: string;
  slug: string;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  country: string;
  public_description: string | null;
  website_url: string | null;
  instagram: string | null;
  plan: string;
  events_remaining: number;
  created_at: string;
  logo_url: string | null;
}

interface OrganizationSettingsProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({ onSuccess, onError }) => {
  const { t, i18n } = useTranslation();
  const { user, checkAuth } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  // L'adresse ne s'ouvre qu'une fois le régime TVA déclaré : pour un
  // organisateur assujetti, VIES nous renvoie son adresse officielle et il
  // n'a rien à saisir. Lui faire remplir avant serait du travail jeté.
  const [vatDeclared, setVatDeclared] = useState<boolean | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoWarnings, setLogoWarnings] = useState<string[]>([]);
  const [logoDeleteConfirmOpen, setLogoDeleteConfirmOpen] = useState(false);
  const [organization, setOrganization] = useState<OrganizationData | null>(null);
  // `loadOrganization` est défini avant le hook : la ref lui donne accès
  // à `setBaseline` sans réordonner le fichier.
  const baselineRef = useRef<((v: Record<string, any>) => void) | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    country: 'FR',
    public_description: '',
    website_url: '',
    instagram: '',
  });


  useEffect(() => {
    apiService
      .get('/organizations/me/vat')
      .then((res) => setVatDeclared(Boolean(res.data?.is_declared)))
      .catch(() => setVatDeclared(true)); // en cas d'échec, ne pas bloquer la saisie
  }, []);

  useEffect(() => {
    loadOrganization();
  }, []);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      const res = await apiService.get(`/organizations/${user?.organization?.name || 'current'}`);
      const org = res.data;
      setOrganization(org);
      const data = {
        name: org.name || '',
        slug: org.slug || '',
        contact_email: org.contact_email || org.email || '',
        contact_phone: org.contact_phone || '',
        address: org.address || '',
        country: org.country || 'FR',
        public_description: org.public_description || '',
        website_url: org.website_url || '',
        instagram: org.instagram || '',
      };
      setFormData(data);
      // Ce que le serveur porte : `commitIfChanged` s'y compare pour ne pas
      // réécrire un champ qu'on a seulement traversé au clavier.
      baselineRef.current?.(data);
    } catch (err: any) {
      onError?.(t('settings.orgSettings.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * L'écran s'enregistre au fur et à mesure : chaque champ part au blur, le
   * pays au choix. Le `PUT /organizations/me` est déjà PARTIEL (il ne touche
   * que les clés présentes), donc un champ en cours de frappe n'écrase rien.
   *
   * Le NOM et le SLUG restent des champs comme les autres : ils changent
   * l'adresse publique de l'organisation, mais c'est justement ce qu'on vient
   * faire ici — l'écran ne sert qu'à ça. Le serveur refuse un slug déjà pris
   * (409), et le refus est visible.
   */
  const auto = useAutoSave<Record<string, any>>(useCallback(async (patch) => {
    await apiService.updateOrganization(patch);
    // La barre du haut porte le nom de l'organisation : elle doit suivre.
    await checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []), {
    onError: (err: any) =>
      onError?.(err?.response?.data?.detail || t('settings.orgSettings.messages.saveError')),
  });
  baselineRef.current = auto.setBaseline;

  /** Champ texte : écrit au blur, et seulement s'il a bougé. */
  const champ = (cle: string, valeur: string) => ({
    onBlur: () => {
      void auto.commitIfChanged(cle, valeur);
    },
  });

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier le type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      onError?.(t('settings.orgSettings.logo.invalidType'));
      return;
    }

    // Vérifier la taille (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      onError?.(t('settings.orgSettings.logo.tooLarge'));
      return;
    }

    try {
      setUploadingLogo(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      // axios détecte automatiquement multipart/form-data avec FormData
      const response = await apiService.post('/organizations/logo', uploadFormData);

      // Mettre à jour l'organisation avec la nouvelle URL
      setOrganization(prev => prev ? { ...prev, logo_url: response.data.logo_url } : null);
      setLogoWarnings(response.data.warnings || []);
      onSuccess?.(t('settings.orgSettings.logo.uploaded'));
    } catch (err: any) {
      setLogoWarnings([]);
      onError?.(err.response?.data?.detail || t('settings.orgSettings.logo.uploadError'));
    } finally {
      setUploadingLogo(false);
      // Reset l'input file pour permettre de re-sélectionner le même fichier
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoDelete = () => setLogoDeleteConfirmOpen(true);

  const confirmLogoDelete = async () => {
    setLogoDeleteConfirmOpen(false);
    try {
      setUploadingLogo(true);
      await apiService.delete('/organizations/logo');
      setOrganization(prev => prev ? { ...prev, logo_url: null } : null);
      setLogoWarnings([]);
      onSuccess?.(t('settings.orgSettings.logo.deleted'));
    } catch (err: any) {
      onError?.(err.response?.data?.detail || t('settings.orgSettings.logo.deleteError'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // Construire l'URL complète du logo
  const getLogoUrl = () => {
    if (!organization?.logo_url) return null;
    // Si l'URL commence par /api, ajouter le base URL
    if (organization.logo_url.startsWith('/api')) {
      const baseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'https://events.logfit.eu';
      return `${baseUrl}${organization.logo_url}`;
    }
    return organization.logo_url;
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Colonne gauche */}
        <div className="space-y-4">
          {/* Carte 1 : Informations générales */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t('settings.orgSettings.generalInfo.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('settings.orgSettings.generalInfo.name')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  {...champ('name', formData.name)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">{t('settings.orgSettings.generalInfo.slug')}</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  {...champ('slug', formData.slug)}
                />
                <p className="text-xs text-muted-foreground">
                  {t('settings.orgSettings.generalInfo.slugUrl', { slug: formData.slug || 'your-slug' })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Carte : page publique de l'organisation (LOG-171).
              C'est la description qui décide de l'existence de la page — vide,
              l'adresse rend un 404. La ligne d'état le dit en toutes lettres :
              sans elle, l'organisateur ne peut pas deviner ce que déclenche ce
              champ. */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t('settings.orgSettings.publicPage.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="public_description">
                  {t('settings.orgSettings.publicPage.description')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('settings.orgSettings.publicPage.descriptionHint')}
                </p>
                <Textarea
                  id="public_description"
                  rows={4}
                  value={formData.public_description}
                  onChange={(e) => setFormData({ ...formData, public_description: e.target.value })}
                  {...champ('public_description', formData.public_description)}
                  placeholder={t('settings.orgSettings.publicPage.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="website_url">{t('settings.orgSettings.publicPage.website')}</Label>
                  <Input
                    id="website_url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  {...champ('website_url', formData.website_url)}
                    placeholder="https://"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instagram">{t('settings.orgSettings.publicPage.instagram')}</Label>
                  <Input
                    id="instagram"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  {...champ('instagram', formData.instagram)}
                    placeholder="@"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
                {formData.public_description.trim() ? (
                  <>
                    <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-success shadow-[0_0_0_3px_rgba(34,197,94,0.16)]" />
                    <span className="text-muted-foreground">
                      {t('settings.orgSettings.publicPage.live')}
                    </span>
                    <a
                      href={`${PUBLIC_SITE_URL}/fr/${formData.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-foreground underline underline-offset-4"
                    >
                      {`${PUBLIC_SITE_URL.replace(/^https?:\/\//, '')}/fr/${formData.slug}`}
                    </a>
                  </>
                ) : (
                  <>
                    <span className="h-[7px] w-[7px] shrink-0 rounded-full border-[1.5px] border-neutral-500" />
                    <span className="text-muted-foreground">
                      {t('settings.orgSettings.publicPage.notPublished')}
                    </span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Carte 2 : Coordonnées */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t('settings.orgSettings.contact.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">{t('settings.orgSettings.contact.email')}</Label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="contact_email"
                    type="email"
                    className="pl-10"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  {...champ('contact_email', formData.contact_email)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">{t('settings.orgSettings.contact.phone')}</Label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="contact_phone"
                    className="pl-10"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  {...champ('contact_phone', formData.contact_phone)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">{t('settings.orgSettings.contact.address')}</Label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-3 top-3 text-muted-foreground" />
                  <Textarea
                    id="address"
                    className="pl-10 min-h-[80px]"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  {...champ('address', formData.address)}
                    disabled={vatDeclared === false}
                  />
                </div>
                {vatDeclared === false && (
                  <p className="text-xs text-muted-foreground">
                    {t('settings.orgSettings.contact.addressNeedsVat')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colonne droite */}
        <div className="space-y-4">
          {/* Carte Logo */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t('settings.orgSettings.logo.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                className="hidden"
              />

              {getLogoUrl() ? (
                <div className="space-y-3">
                  <div className="relative w-32 h-32 rounded-lg border border-border overflow-hidden bg-muted">
                    <img
                      src={getLogoUrl()!}
                      alt={t('settings.orgSettings.logo.alt')}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <Loader2 size={14} className="animate-spin mr-2" />
                      ) : (
                        <Upload size={14} className="mr-2" />
                      )}
                      {t('settings.orgSettings.logo.change')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLogoDelete}
                      disabled={uploadingLogo}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={14} className="mr-2" />
                      {t('settings.orgSettings.logo.delete')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => !uploadingLogo && fileInputRef.current?.click()}
                  className="w-full h-32 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                >
                  {uploadingLogo ? (
                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <Upload size={24} className="text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">{t('settings.orgSettings.logo.clickToUpload')}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('settings.orgSettings.logo.maxSize')}</p>
                    </>
                  )}
                </div>
              )}
              <LogoWarnings codes={logoWarnings} />
            </CardContent>
          </Card>

          {/* Carte 3 : Localisation */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t('settings.orgSettings.location.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="country">{t('settings.orgSettings.location.country')}</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => {
                  const avant = formData.country;
                  setFormData({ ...formData, country: value });
                  void auto.commit({ country: value },
                    () => setFormData(f => ({ ...f, country: avant })));
                }}
              >
                <SelectTrigger id="country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryOptions(i18n.language).map(({ code, name }) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground pt-1">
                {t('settings.orgSettings.location.countryHint')}
              </p>
              {NON_EURO_COUNTRY_CODES.includes(formData.country) && (
                <p className="text-xs text-muted-foreground">
                  {t('settings.orgSettings.location.nonEuroHint')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Carte 4 : Plan */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t('settings.orgSettings.plan.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('settings.orgSettings.plan.currentPlan')}</p>
                  <Badge
                    className={
                      organization?.plan === 'premium' || organization?.plan === 'pro'
                        ? 'bg-white/[0.10] text-foreground border-white/30'
                        : ''
                    }
                  >
                    {organization?.plan?.toUpperCase() || 'FREE'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('settings.orgSettings.plan.memberSince')}</p>
                  <p className="text-sm">
                    {organization?.created_at
                      ? new Date(organization.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* L'écran s'enregistre tout seul : l'ÉTAT remplace les boutons.
          « Annuler » a disparu avec eux — il ne pouvait plus rien annuler. */}
      <div className="mt-8 pt-6 border-t border-border flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">{t('autoSave.hint')}</span>
        <AutoSaveIndicator status={auto.status} retry={auto.retry} />
      </div>

      <ConfirmDialog
        open={logoDeleteConfirmOpen}
        onOpenChange={setLogoDeleteConfirmOpen}
        title={t('settings.orgSettings.logo.deleteTitle')}
        message={t('settings.orgSettings.logo.deleteConfirm')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={confirmLogoDelete}
      />
    </div>
  );
};

export default OrganizationSettings;
